import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import { SegmentResponse } from '@/api/type'

/* eslint-disable @typescript-eslint/no-explicit-any */
type UseSegmentEditorProps = {
  transcription: any
  setTranscription: (transcription: any) => void
}

type UseSegmentEditorReturn = {
  swipedSegmentIndex: number | null
  setSwipedSegmentIndex: (index: number | null) => void
  longPressedWord: {
    word: { word: string; start: number; end: number }
    wordIndex: number
    segmentIndex: number
  } | null
  setLongPressedWord: (
    word: {
      word: { word: string; start: number; end: number }
      wordIndex: number
      segmentIndex: number
    } | null,
  ) => void
  editingWordText: string
  setEditingWordText: (text: string) => void
  mergeWithNext: (index: number) => void
  handleTouchStart: (e: React.TouchEvent) => void
  handleTouchEnd: (e: React.TouchEvent, index: number) => void
  handleWordTouchStart: (
    word: { word: string; start: number; end: number },
    wordIndex: number,
    segmentIndex: number,
  ) => void
  handleWordTouchEnd: () => void
  handleEditWord: () => void
  toggleSkipSegment: (index: number, e: React.SyntheticEvent) => void
  handleSplitSegment: () => void
}

/**
 * セグメント編集機能を管理するカスタムフック
 * セグメントの結合、分割、単語編集、スキップトグルを提供
 */
export const useSegmentEditor = ({
  transcription,
  setTranscription,
}: UseSegmentEditorProps): UseSegmentEditorReturn => {
  const [swipedSegmentIndex, setSwipedSegmentIndex] = useState<number | null>(null)
  const [longPressedWord, setLongPressedWord] = useState<{
    word: { word: string; start: number; end: number }
    wordIndex: number
    segmentIndex: number
  } | null>(null)
  const [editingWordText, setEditingWordText] = useState('')

  const touchStartX = useRef(0)
  const touchStartY = useRef(0)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // セグメントを下のセグメントと結合
  const mergeWithNext = useCallback(
    (index: number) => {
      if (!transcription || index >= transcription.segments.length - 1) return

      const currentSegment = transcription.segments.at(index)
      const nextSegment = transcription.segments.at(index + 1)
      if (!currentSegment || !nextSegment) return

      // 2つのセグメントを結合
      const mergedSegment: SegmentResponse = {
        start: currentSegment.start,
        end: nextSegment.end,
        text: `${currentSegment.text} ${nextSegment.text}`,
        words: [...(currentSegment.words || []), ...(nextSegment.words || [])],
        seek: currentSegment.seek,
        no_speech_prob: Math.min(currentSegment.no_speech_prob, nextSegment.no_speech_prob),
      }

      // 新しいセグメント配列を作成
      const newSegments = [
        ...transcription.segments.slice(0, index),
        mergedSegment,
        ...transcription.segments.slice(index + 2),
      ]

      setTranscription({
        ...transcription,
        segments: newSegments,
      })

      setSwipedSegmentIndex(null)
      toast.success('セグメントを結合しました')
    },
    [transcription, setTranscription],
  )

  // タッチスワイプハンドラー
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent, index: number) => {
    const touchEndX = e.changedTouches[0].clientX
    const touchEndY = e.changedTouches[0].clientY
    const deltaX = touchStartX.current - touchEndX
    const deltaY = Math.abs(touchStartY.current - touchEndY)

    // 左スワイプ（横方向の移動が縦方向より大きい）
    if (deltaX > 50 && deltaY < 50) {
      setSwipedSegmentIndex(index)
    }
    // 右スワイプでクローズ
    else if (deltaX < -50 && deltaY < 50) {
      setSwipedSegmentIndex(null)
    }
  }, [])

  // 単語のロングタップハンドラー
  const handleWordTouchStart = useCallback(
    (word: { word: string; start: number; end: number }, wordIndex: number, segmentIndex: number) => {
      if (transcription?.segments.at(segmentIndex)?.skipped) return

      longPressTimer.current = setTimeout(() => {
        setLongPressedWord({ word, wordIndex, segmentIndex })
        setEditingWordText(word.word)
      }, 500)
    },
    [transcription],
  )

  const handleWordTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  // 単語の文字列を編集
  const handleEditWord = useCallback(() => {
    if (!longPressedWord || !transcription) return

    const { wordIndex, segmentIndex } = longPressedWord
    const segment = transcription.segments.at(segmentIndex)
    if (!segment) return

    const words = segment.words || []
    const targetWord = words.at(wordIndex)
    if (!targetWord) return

    const updatedWords = [
      ...words.slice(0, wordIndex),
      { ...targetWord, word: editingWordText },
      ...words.slice(wordIndex + 1),
    ]

    const updatedSegment = {
      ...segment,
      words: updatedWords,
      text: updatedWords.map((w) => w.word).join(' '),
    }

    setTranscription({
      ...transcription,
      segments: [
        ...transcription.segments.slice(0, segmentIndex),
        updatedSegment,
        ...transcription.segments.slice(segmentIndex + 1),
      ],
    })

    setLongPressedWord(null)
    toast.success('単語を編集しました')
  }, [longPressedWord, transcription, editingWordText, setTranscription])

  // チェックボックスのトグル
  const toggleSkipSegment = useCallback(
    (index: number, e: React.SyntheticEvent) => {
      e.stopPropagation()
      if (!transcription) return

      const segment = transcription.segments.at(index)
      if (!segment) return

      setTranscription({
        ...transcription,
        segments: [
          ...transcription.segments.slice(0, index),
          { ...segment, skipped: !segment.skipped },
          ...transcription.segments.slice(index + 1),
        ],
      })
    },
    [transcription, setTranscription],
  )

  // その位置で文を分割
  const handleSplitSegment = useCallback(() => {
    if (!longPressedWord || !transcription) return

    const { wordIndex, segmentIndex } = longPressedWord
    const segment = transcription.segments.at(segmentIndex)
    if (!segment) return

    const words = segment.words || []

    if (wordIndex === 0) {
      toast.error('最初の単語では分割できません')
      return
    }

    // 前半と後半に分割
    const firstWords = words.slice(0, wordIndex)
    const secondWords = words.slice(wordIndex)

    const firstSegment: SegmentResponse = {
      start: segment.start,
      end: firstWords[firstWords.length - 1].end,
      text: firstWords.map((w: any) => w.word).join(' '),
      words: firstWords,
      seek: segment.seek,
      no_speech_prob: segment.no_speech_prob,
    }

    const secondSegment: SegmentResponse = {
      start: secondWords[0].start,
      end: segment.end,
      text: secondWords.map((w: any) => w.word).join(' '),
      words: secondWords,
      seek: segment.seek,
      no_speech_prob: segment.no_speech_prob,
    }

    const newSegments = [
      ...transcription.segments.slice(0, segmentIndex),
      firstSegment,
      secondSegment,
      ...transcription.segments.slice(segmentIndex + 1),
    ]

    setTranscription({
      ...transcription,
      segments: newSegments,
    })

    setLongPressedWord(null)
    toast.success('文を分割しました')
  }, [longPressedWord, transcription, setTranscription])

  return {
    swipedSegmentIndex,
    setSwipedSegmentIndex,
    longPressedWord,
    setLongPressedWord,
    editingWordText,
    setEditingWordText,
    mergeWithNext,
    handleTouchStart,
    handleTouchEnd,
    handleWordTouchStart,
    handleWordTouchEnd,
    handleEditWord,
    toggleSkipSegment,
    handleSplitSegment,
  }
}
